import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';
import { STORAGE_KEYS } from '../../utils/storageKeys';

// Define types for request and response
export interface OrganizationCreateInput {
  org_name: string;
  org_display_name: string;
  admin_email: string;
}

export interface OrganizationCreateResponse {
  organization_id: string;
  user_id: string;
  invitation_id: string;
  message: string;
}

// Define types for organization with admin creation
export interface OrganizationFormData {
  name: string;
  slug: string;
  description?: string;
  status: 'active' | 'inactive';
  sso_enabled: boolean;
  sso_settings?: {
    connection_name: string;
    display_name: string;
    tenant_domain: string;
    client_id: string;
    client_secret: string;
    use_common_endpoint: boolean;
    identity_api_version: string;
  } | {};
  infra_provider: string;
  infra_region: string;
  infra_config: {
    resources: {
      [key: string]: Record<string, any>;
    };
  };
}

export interface AdminUserFormData {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  mobile_number?: string;
  roleId?: string; // Make roleId optional as it will be set in the backend
}

export interface CreateOrgWithAdminRequest {
  organization: OrganizationFormData;
  admin: AdminUserFormData;
}

export interface CreateOrgResponse {
  organization: {
    id: string;
    auth0_org_id: string;
    name: string;
    status: string;
  };
  admin: {
    id: string;
    auth0_user_id: string;
    email: string;
  };
  message: string;
}

export interface Organization {
  [x: string]: any;
  id: string;
  name: string;
  display_name: string;
  branding?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface ProvisioningDetail {
  id: string;
  client_organization: string;
  step_name: string;
  step_description: string;
  step_order: number;
  status: string;
  error_message: string | null;
  retry_count: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProvisioningDetailsResponse {
  count: number;
  limit: number;
  next: string | null;
  previous: string | null;
  results: ProvisioningDetail[];
}

export interface AuditLog {
  id: string;
  client_organization: string;
  action: string;
  action_type: string;
  resource_type: string;
  resource_id: string;
  message: string | null;
  details: any | null;
  details_string: string | null;
  user_email: string;
  ip_address: string | null;
  timestamp: string;
}

export interface AuditLogsResponse {
  count: number;
  limit: number;
  next: string | null;
  previous: string | null;
  results: AuditLog[];
}

export interface MigrationHistoryResponse {
  results: [string, string, string][];
}

export interface OrganizationListResponse {
  count: number;
  limit: number;
  next: string | null;
  previous: string | null;
  results: Organization[];
}

export interface OrganizationListParams {
  page?: number;
  per_page?: number;
  id?: string;
  name?: string;
  slug?: string;
  description?: string;
  sso_enabled?: string;
  sso_provider?: string;
  is_active?: string;
  user_count?: string;
  created_at?: string;
  created_at_after?: string;
  created_at_before?: string;
  provisioning_status?: string;
  organization_id?: string;
}

interface FormBuilderSchema {
  region: {
    enum?: string[];
    title?: string;
    type?: string;
  }
  required: string[];
  resources: {
    [key: string]: Record<string, any>;
  };
}

interface InfraProvider {
  created_at: string | null;
  display_name: string; 
  id: string;
  identifier: string;
  identifier_type: string;
  form_builder_schema: FormBuilderSchema;
  is_active: boolean;
  provider: string;
  region: string;
  updated_at: string | null;
  resources: {
    [key: string]: Record<string, any>;
  };
}

export interface OrganizationDetail {
  id: string;
  name: string;
  description: string | null;
  slug: string;
  organization_id: string;
  sso_enabled: boolean;
  sso_provider: string | null;
  sso_connection_id: string | null;
  sso_settings: any | null;
  provisioning_status: string;
  provisioning_started_at: string | null;
  provisioning_completed_at: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_phone_detail?: {
    dial_code: string;
    national_number: string;
  } | null;
  subscription_plan: string | null;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
  custom_metadata: any | null;
  is_active: boolean;
  created_at: string;
  created_by_detail: {
    id: string;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_active: boolean;
    is_superuser: boolean;
    is_staff: boolean;
    auth0_user_id: string;
  } | null;
  updated_at: string;
  updated_by_detail: any | null;
  user_count: number;
  display_name?: string;
  message?: string;
  infra_provider?: InfraProvider;
  feature_flags?: Record<string, any>;
  feature_flags_schema?: FeatureFlagsSchema;
  profile: {
    currency_code: string | null;
  };
}

export interface Industry {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
}

export interface ParentCompany {
  id: string;
  name: string;
}

export interface InfraConfigResponse {
  region: {
    type: string;
    enum: string[];
    title: string;
  };
  resources: {
    database: {
      type: string;
      properties: Record<string, {
        type: string;
        enum?: any[];
        title: string;
        min?: number;
        max?: number;
        default?: any;
      }>;
      required: string[];
    }
  };
}

export interface FeatureFlagsSchema {
  properties: Record<string, {
    type: string;
    enum?: any[];
    title: string;
    hint?: string;
    default?: any;
    editable?: boolean;
  }>;
  required: string[];
}

export interface OrganizationSpecificAction {
  name: string;
  value: string;
}

export interface OrganizationActionRequest {
  action: 'specific_action';
  params: {
    action: string;
  };
}

export interface CurrencyConfig {
  code: string;
  name: string;
  symbol: string;
}

// Create API
export const organizationApi = createApi({
  reducerPath: 'organizationApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['Organization'],
  endpoints: (builder) => ({
    createOrganizationAndInvite: builder.mutation<
      OrganizationCreateResponse,
      OrganizationCreateInput
    >({
      query: (data) => ({
        url: 'auth0/organizations/create-and-invite',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Organization'],
    }),
    createOrganizationWithAdmin: builder.mutation<
      CreateOrgResponse,
      FormData
    >({
      query: (data) => ({
        url: 'auth0/organizations/create-with-admin/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Organization'],
    }),
    listOrganizations: builder.query<
      OrganizationListResponse,
      OrganizationListParams
    >({
      query: (params) => ({
        url: 'auth0/organizations/list/',
        method: 'GET',
        params: {
          paginate: true,
          page: params?.page ?? 0,
          limit: params?.per_page ?? 10,
          id: params?.id,
          name: params?.name,
          slug: params?.slug,
          description: params?.description,
          sso_enabled: params?.sso_enabled,
          sso_provider: params?.sso_provider,
          is_active: params?.is_active,
          user_count: params?.user_count,
          created_at: params?.created_at,
          created_at_after: params?.created_at_after,
          created_at_before: params?.created_at_before,
          provisioning_status: params?.provisioning_status,
          organization_id: params?.organization_id,
        },
      }),
      providesTags: ['Organization'],
    }),
    updateOrganization: builder.mutation<
      OrganizationDetail,
      { id: string; data: FormData }
    >({
      query: ({ id, data }) => ({
        url: `auth0/organizations/edit/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Organization', id },
      ],
    }),

    getOrganizationDetail: builder.query<OrganizationDetail, string>({
      query: (id) => `auth0/organizations/view/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Organization', id }],
    }),
    listIndustries: builder.query<{ results: Industry[] }, void>({
      query: () => 'industries/',
    }),
    listParentCompanies: builder.query<{ results: ParentCompany[] }, void>({
      query: () => 'companies/parent-companies/',
    }),
    updateCompanyProfile: builder.mutation<
      any,
      {
        companyId: string;
        formData: FormData;
      }
    >({
      query: ({ companyId, formData }) => ({
        url: `company/companies/${companyId}/profile/`,
        method: 'PATCH',
        body: formData,
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)}`,
        },
      }),
      invalidatesTags: ['Organization'],
    }),
    performOrganizationAction: builder.mutation<
      { message: string },
      {
        organizationId: string;
        action: 'enable_organization' | 'disable_organization' | 'run_migration' | 'run_provisioning' | 'specific_actions';
      }
    >({
      query: ({ organizationId, action }) => ({
        url: `client-organizations/${organizationId}/actions/`,
        method: 'POST',
        body: { action },
      }),
      invalidatesTags: (result, error, { organizationId }) => [
        { type: 'Organization', id: organizationId },
      ],
    }),
    getOrganizationSpecificAction: builder.query<
      OrganizationSpecificAction,
      { organizationId: string }
    >({
      query: ({ organizationId}) =>
        `client-organizations/${organizationId}/specific_actions/`,
      providesTags: (result, error, arg) => [
        { type: 'Organization', id: arg.organizationId },
      ],
    }),
    specificOrganizationAction: builder.mutation<
      { message: string },
      {
        organizationId: String;
        action: OrganizationActionRequest;
      }
    >({
      query: ({ organizationId, action }) => ({
        url: `client-organizations/${organizationId}/actions/`,
        method: 'POST',
        body: action ,
      }),
    }),
    getProvisioningDetails: builder.query<
      ProvisioningDetailsResponse,
      { organizationId: string; page?: number; limit?: number }
    >({
      query: ({ organizationId, page = 1, limit = 10 }) =>
        `client-organizations/${organizationId}/provisioning_details/?page=${page}&limit=${limit}`,
      providesTags: (result, error, arg) => [
        { type: 'Organization', id: arg.organizationId },
      ],
    }),
    getAuditLogs: builder.query<
      AuditLogsResponse,
      { organizationId: string; page?: number; limit?: number }
    >({
      query: ({ organizationId, page = 1, limit = 10 }) =>
        `client-organizations/${organizationId}/audit_logs/?page=${page}&limit=${limit}`,
      providesTags: (result, error, arg) => [
        { type: 'Organization', id: arg.organizationId },
      ],
    }),
    getMigrationHistory: builder.query<
      MigrationHistoryResponse,
      string
    >({
      query: (organizationId) =>
        `client-organizations/${organizationId}/migration_history/`,
      providesTags: (result, error, id) => [{ type: 'Organization', id }],
    }),
    getInfraConfig: builder.query<
      InfraConfigResponse,
      string
    >({
      query: (provider) =>
        `client-organizations/infra_config/?provider=${provider}`,
    }),
    getFeatureFlagsSchema: builder.query<
      FeatureFlagsSchema,
      string
    >({
      query: (provider) =>
        `client-organizations/feature_flags_schema/?provider=${provider}`,
    }),
    getCurrencySymbol: builder.query<
      CurrencyConfig,
      void
    >({
      query: () => 'client-organizations/get_currencies/',
    }),
  }),
});

// Export hooks
export const {
  useCreateOrganizationAndInviteMutation,
  useCreateOrganizationWithAdminMutation,
  useListOrganizationsQuery,
  useGetOrganizationDetailQuery,
  useUpdateOrganizationMutation,
  useListIndustriesQuery,
  useListParentCompaniesQuery,
  useUpdateCompanyProfileMutation,
  usePerformOrganizationActionMutation,
  useGetOrganizationSpecificActionQuery,
  useSpecificOrganizationActionMutation,
  useGetProvisioningDetailsQuery,
  useGetAuditLogsQuery,
  useGetMigrationHistoryQuery,
  useGetInfraConfigQuery,
  useGetFeatureFlagsSchemaQuery,
  useGetCurrencySymbolQuery,
} = organizationApi;
